import { useEffect, useRef } from 'react';

import type { TinyAIPageContext } from '@repo/types';

import { useAnalytics } from '../../../contexts/analytics/AnalyticsProvider';
import { PendingPrompt, useTinyAIContext } from '../TinyAIContext';

interface UseTinyAIPendingPromptDispatcherOptions {
  sessionId: string | null;
  isLoadingHistory: boolean;
  sendMessage: (
    content: string,
    isPredefinedPrompt?: boolean,
    promptId?: number,
    pageContextOverride?: TinyAIPageContext,
  ) => Promise<void>;
}

export const useTinyAIPendingPromptDispatcher = ({
  sessionId,
  isLoadingHistory,
  sendMessage,
}: UseTinyAIPendingPromptDispatcherOptions) => {
  const { consumePendingPrompt, pendingPromptTrigger } = useTinyAIContext();
  const { trackEvent } = useAnalytics();

  const pendingPromptToSendRef = useRef<PendingPrompt | null>(null);

  useEffect(() => {
    if (!sessionId || isLoadingHistory) return;

    const justConsumed = consumePendingPrompt();
    if (justConsumed) {
      pendingPromptToSendRef.current = justConsumed;
    }
    if (!pendingPromptToSendRef.current) return;

    const id = setTimeout(() => {
      const pending = pendingPromptToSendRef.current;
      if (!pending) return;
      pendingPromptToSendRef.current = null;
      trackEvent('TinyAI prompt hook clicked', {
        prompt_text: pending.text,
        display_location: pending.displayLocation,
        prompt_id: pending.promptId,
        page_context: pending.pageContext,
      });
      trackEvent('TinyAI message sent', {
        page_context: pending.pageContext,
        source: 'chat',
        prompt_text: pending.text,
        display_location: pending.displayLocation,
      });
      // Pass click-time page context so metric hooks stay bound to the page
      // the user clicked from, not whatever the chat window last rendered.
      sendMessage(pending.text, true, pending.promptId, pending.pageContext);
    }, 200);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingPromptTrigger, sessionId, isLoadingHistory]);
};
